import axios from 'axios';
import {
  Schedule,
  Holiday,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  ScheduleResponse,
  HolidayResponse,
  SchedulePaginationResponse,
  ScheduleFilters,
  ScheduleSortOptions,
  CalendarEvent,
  ScheduleCategory,
  SchedulePriority,
  ScheduleStatus,
  RecurrenceType
} from '../types/schedule';
import { handleUnauthorized } from '../store/authStore';

// Node.js API 기본 URL
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api2';

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 (인증 토큰 추가)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nas_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 (에러 처리)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

// PHP API 응답을 우리 타입으로 변환하는 함수
const mapPhpResponseToSchedule = (phpData: any): Schedule | null => {


  // PHP API 실제 필드명: id, title, start, end, all_day, description, created_at, color
  const startDate = phpData.start;
  const endDate = phpData.end || phpData.start; // end가 null이면 start 사용

  if (!phpData.id || !phpData.title || !startDate) {
    console.warn('⚠️ PHP API 응답 데이터 불완전:', {
      id: phpData.id,
      title: phpData.title,
      start: phpData.start,
      phpData
    });
    return null;
  }

  try {
    const schedule = {
      id: parseInt(phpData.id),
      title: phpData.title || '',
      description: phpData.description || '',
      start_date: startDate,
      end_date: endDate,
      category: phpData.category || '', // DB에서 한글로 저장된 카테고리를 그대로 사용
      priority: (codeToPriority[phpData.priority] || phpData.priority) as SchedulePriority || SchedulePriority.MEDIUM,
      status: (codeToStatus[phpData.status] || phpData.status) as ScheduleStatus || ScheduleStatus.SCHEDULED,
      location: phpData.location || '',
      participants: phpData.participants || '',
      // recurrence_type 매핑 보완 (php는 'none'|'daily'|... 문자열)
      recurrence_type: (
        phpData.recurrence_type === 'daily' ? RecurrenceType.DAILY :
          phpData.recurrence_type === 'weekly' ? RecurrenceType.WEEKLY :
            phpData.recurrence_type === 'monthly' ? RecurrenceType.MONTHLY :
              phpData.recurrence_type === 'yearly' ? RecurrenceType.YEARLY :
                RecurrenceType.NONE
      ),
      recurrence_end: phpData.recurrence_end || '',
      is_all_day: phpData.all_day === '1' || phpData.all_day === true || phpData.all_day === 1,
      created_by: phpData.created_by || 'system',
      created_at: phpData.created_at || new Date().toISOString(),
      updated_at: phpData.updated_at,
      // PHP API의 color 필드를 임시로 저장 (Schedule interface에는 없지만 변환 시 사용)
      ...(phpData.color && { color: phpData.color })
    } as Schedule & { color?: string };


    return schedule;
  } catch (error: any) {
    console.error('❌ PHP 응답 변환 실패:', {
      phpData,
      error: error?.message || 'Unknown error'
    });
    return null;
  }
};

const mapPhpResponseToHoliday = (phpData: any): Holiday => {
  // API 응답 형식에 맞게 매핑
  return {
    id: parseInt(phpData.id?.replace('holiday-', '') || phpData.id),
    name: phpData.title || phpData.name,
    date: phpData.start || phpData.date,
    type: 'national', // 공휴일은 국경일로 설정
    is_holiday: true, // 공휴일은 항상 휴일
    description: phpData.description || '',
    created_at: phpData.created_at || new Date().toISOString(),
  };
};

// 카테고리를 영문 코드로 매핑
const categoryToCode: Record<string, string> = {
  '외출': 'user_outing',
  '외박': 'user_overnight',
  '면회': 'user_visit',
  '자립': 'user_independence',
  '퇴소': 'user_discharge',
  '연가': 'staff_annual',
  '공가': 'staff_public',
  '병가': 'staff_sick',
  '반가': 'staff_half',
  '가족돌봄': 'staff_family_care',
  '회의': 'meeting',
  '행사': 'event',
  '교육': 'training',
  '점검': 'maintenance'
};

// 영문 코드를 한글로 역매핑
const codeToCategory: Record<string, string> = {
  'user_outing': '외출',
  'user_overnight': '외박',
  'user_visit': '면회',
  'user_independence': '자립',
  'user_discharge': '퇴소',
  'staff_annual': '연가',
  'staff_public': '공가',
  'staff_sick': '병가',
  'staff_half': '반가',
  'staff_family_care': '가족돌봄',
  'meeting': '회의',
  'event': '행사',
  'training': '교육',
  'maintenance': '점검'
};

// 우선순위를 영문 코드로 매핑
const priorityToCode: Record<string, string> = {
  '낮음': 'low',
  '보통': 'medium',
  '높음': 'high',
  '긴급': 'urgent'
};

// 영문 코드를 한글로 역매핑
const codeToPriority: Record<string, string> = {
  'low': '낮음',
  'medium': '보통',
  'high': '높음',
  'urgent': '긴급'
};

// 상태를 영문 코드로 매핑
const statusToCode: Record<string, string> = {
  '예정': 'scheduled',
  '진행중': 'in_progress',
  '완료': 'completed',
  '취소': 'cancelled',
  '연기': 'postponed'
};

// 영문 코드를 한글로 역매핑
const codeToStatus: Record<string, string> = {
  'scheduled': '예정',
  'in_progress': '진행중',
  'completed': '완료',
  'cancelled': '취소',
  'postponed': '연기'
};

// 카테고리별 색상 매핑 함수
const getCategoryColor = (category: string): string => {
  const categoryColors: Record<string, string> = {
    '외출': '#3B82F6',
    '외박': '#1E40AF',
    '면회': '#60A5FA',
    '자립': '#2563EB',
    '퇴소': '#DC2626',
    '연가': '#10B981',
    '공가': '#059669',
    '병가': '#EF4444',
    '반가': '#34D399',
    '가족돌봄': '#F59E0B',
    '회의': '#8B5CF6',
    '행사': '#EC4899',
    '교육': '#06B6D4',
    '점검': '#6B7280'
  };

  return categoryColors[category] || '#3B82F6'; // 기본값은 외출 색상
};

// 우리 타입을 PHP API 형식으로 변환하는 함수
const mapScheduleToPhpRequest = (data: CreateScheduleRequest | UpdateScheduleRequest) => {


  // 카테고리별 색상 자동 설정
  const categoryColor = getCategoryColor(data.category);

  // 모든 필드를 포함하여 전송
  const phpData: any = {
    title: data.title,
    description: data.description || '',
    start: data.start_date,
    end: data.end_date,
    all_day: data.is_all_day ? 1 : 0,
    category: data.category || '', // 한글로 직접 저장
    color: categoryColor, // 카테고리별 색상 자동 설정
    priority: priorityToCode[data.priority] || 'medium',
    // recurrence_type 매핑 (enum -> 코드)
    recurrence_type: (
      data.recurrence_type === RecurrenceType.DAILY ? 'daily' :
        data.recurrence_type === RecurrenceType.WEEKLY ? 'weekly' :
          data.recurrence_type === RecurrenceType.MONTHLY ? 'monthly' :
            data.recurrence_type === RecurrenceType.YEARLY ? 'yearly' :
              'none'
    ),
    location: data.location || '',
    participants: data.participants || '',
    ...(('id' in data) && { id: data.id }),
    ...(('status' in data) && { status: statusToCode[data.status] || 'scheduled' }),
  };

  // recurrence_end는 값이 있을 때만 포함 (빈 문자열이면 제외)
  if (data.recurrence_end && data.recurrence_end.trim() !== '') {
    phpData.recurrence_end = data.recurrence_end;
  }

  return phpData;
};

// Schedule을 CalendarEvent로 변환
const mapScheduleToCalendarEvent = (schedule: Schedule): CalendarEvent | null => {
  // 필수 데이터 검증
  if (!schedule.start_date || !schedule.end_date || !schedule.title) {
    console.warn('⚠️ 일정 데이터 불완전:', schedule);
    return null;
  }

  // 카테고리별 색상 매핑 (더 명확하고 일관된 색상)
  const categoryColors: Record<string, string> = {
    // ScheduleCategory enum 값들 (한글 문자열)
    [ScheduleCategory.USER_OUTING]: '#3B82F6', // 외출 - 파란색
    [ScheduleCategory.USER_OVERNIGHT]: '#1E40AF', // 외박 - 진한 파란색
    [ScheduleCategory.USER_VISIT]: '#60A5FA', // 면회 - 밝은 파란색
    [ScheduleCategory.USER_INDEPENDENCE]: '#2563EB', // 자립 - 중간 파란색
    [ScheduleCategory.USER_DISCHARGE]: '#DC2626', // 퇴소 - 빨간색 (중요하므로)

    [ScheduleCategory.STAFF_ANNUAL]: '#10B981', // 연가 - 초록색
    [ScheduleCategory.STAFF_PUBLIC]: '#059669', // 공가 - 진한 초록색
    [ScheduleCategory.STAFF_SICK]: '#EF4444', // 병가 - 빨간색
    [ScheduleCategory.STAFF_HALF]: '#34D399', // 반가 - 밝은 초록색
    [ScheduleCategory.STAFF_FAMILY_CARE]: '#F59E0B', // 가족돌봄 - 주황색

    [ScheduleCategory.MEETING]: '#8B5CF6', // 회의 - 보라색
    [ScheduleCategory.EVENT]: '#EC4899', // 행사 - 핑크색
    [ScheduleCategory.TRAINING]: '#06B6D4', // 교육 - 청록색
    [ScheduleCategory.MAINTENANCE]: '#6B7280', // 점검 - 회색

    // 영문 코드 매핑 (PHP API에서 사용할 수 있는 경우)
    'user_outing': '#3B82F6',
    'user_overnight': '#1E40AF',
    'user_visit': '#60A5FA',
    'user_independence': '#2563EB',
    'user_discharge': '#DC2626',
    'staff_annual': '#10B981',
    'staff_public': '#059669',
    'staff_sick': '#EF4444',
    'staff_half': '#34D399',
    'staff_family_care': '#F59E0B',
    'meeting': '#8B5CF6',
    'event': '#EC4899',
    'training': '#06B6D4',
    'maintenance': '#6B7280',

    // 기본 색상
    'default': '#3B82F6'
  };

  try {
    // 날짜 문자열 안전하게 변환
    let startDateStr = schedule.start_date;
    let endDateStr = schedule.end_date;

    // 공백을 T로 치환 (YYYY-MM-DD HH:mm:ss -> YYYY-MM-DDTHH:mm:ss)
    if (typeof startDateStr === 'string' && startDateStr.includes(' ')) {
      startDateStr = startDateStr.replace(' ', 'T');
    }
    if (typeof endDateStr === 'string' && endDateStr.includes(' ')) {
      endDateStr = endDateStr.replace(' ', 'T');
    }

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    // 유효한 날짜인지 확인
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn('⚠️ 유효하지 않은 날짜:', {
        title: schedule.title,
        start_date: schedule.start_date,
        end_date: schedule.end_date
      });
      return null;
    }

    // 색상 결정 로직 개선
    let eventColor = categoryColors['default'];

    if (schedule.category) {

      // 1. 정확한 카테고리 매칭 시도 (ScheduleCategory enum 값 또는 영문 코드)
      if (categoryColors[schedule.category]) {
        eventColor = categoryColors[schedule.category];
      }
      // 2. 소문자 매칭 시도 (영문 코드의 경우)
      else if (categoryColors[schedule.category.toLowerCase()]) {
        eventColor = categoryColors[schedule.category.toLowerCase()];
      }
      else {
        console.warn('⚠️ 카테고리에 해당하는 색상을 찾을 수 없음:', {
          category: schedule.category,
          availableColors: Object.keys(categoryColors)
        });
      }
    }

    // PHP color 필드가 있고 카테고리 색상이 기본값인 경우에만 사용
    if ((schedule as any).color && eventColor === categoryColors['default']) {
      eventColor = (schedule as any).color;
    }


    return {
      id: schedule.id,
      title: schedule.title,
      start: startDate,
      end: endDate,
      allDay: schedule.is_all_day,
      color: eventColor,
      backgroundColor: eventColor,
      borderColor: eventColor,
      textColor: '#FFFFFF',
      category: schedule.category,
      priority: schedule.priority,
      status: schedule.status,
      description: schedule.description,
      location: schedule.location,
      participants: schedule.participants,
    };
  } catch (error: any) {
    console.error('❌ 일정 변환 실패:', {
      schedule,
      error: error?.message || 'Unknown error'
    });
    return null;
  }
};

// 색상 기반으로 카테고리를 복구하는 함수
const getCategoryFromColor = (color: string): string => {
  const colorToCategory: Record<string, string> = {
    '#3B82F6': '외출',      // 파란색
    '#1E40AF': '외박',      // 진한 파란색
    '#60A5FA': '면회',      // 밝은 파란색
    '#2563EB': '자립',      // 중간 파란색
    '#DC2626': '퇴소',      // 빨간색
    '#10B981': '연가',      // 초록색
    '#059669': '공가',      // 진한 초록색
    '#EF4444': '병가',      // 빨간색
    '#34D399': '반가',      // 밝은 초록색
    '#F59E0B': '가족돌봄',  // 주황색
    '#8B5CF6': '회의',      // 보라색
    '#EC4899': '행사',      // 핑크색
    '#06B6D4': '교육',      // 청록색
    '#6B7280': '점검'       // 회색
  };

  return colorToCategory[color] || '';
};


export const scheduleApiService = {
  // 오늘 일정 개수 조회
  async getTodaySchedulesCount(): Promise<number> {
    try {
      const today = new Date();
      const todayStr = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0');

      // 전체 데이터를 먼저 가져오기
      const allSchedules = await this.getSchedules();

      // 먼저 날짜 필터로 API 호출 시도
      try {
        const response = await apiClient.get(`/schedule.php?date=${todayStr}`);

        // 전체 데이터 개수와 비교하여 실제 필터링이 되었는지 확인
        const filteredCount = Array.isArray(response.data) ? response.data.length :
          (response.data && response.data.data && Array.isArray(response.data.data) ? response.data.data.length : 0);

        // 필터링이 실제로 되었다면 (개수가 줄어들었다면) 필터링된 결과 사용
        if (filteredCount < allSchedules.length) {
          return filteredCount;
        } else {
          throw new Error('API가 날짜 필터를 지원하지 않음');
        }
      } catch (dateFilterError) {
        // API가 날짜 필터를 지원하지 않으므로 클라이언트에서 필터링
      }

      // 클라이언트에서 오늘 일정 필터링
      const todaySchedules = allSchedules.filter(schedule => {
        try {
          const startDate = new Date(schedule.start_date);

          if (isNaN(startDate.getTime())) {
            return false;
          }

          const startDateStr = startDate.getFullYear() + '-' +
            String(startDate.getMonth() + 1).padStart(2, '0') + '-' +
            String(startDate.getDate()).padStart(2, '0');
          return startDateStr === todayStr;
        } catch (error) {
          return false;
        }
      });

      return todaySchedules.length;
    } catch (error) {
      console.error('❌ 오늘 일정 개수 조회 실패:', error);
      return 0;
    }
  },

  // 일정 목록 조회
  async getSchedules(filters?: ScheduleFilters): Promise<Schedule[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '' && value !== 'ALL') {
            params.append(key, String(value));
          }
        });
      }

      const url = params.toString() ? `/schedule.php?${params.toString()}` : '/schedule.php';
      const response = await apiClient.get(url);

      // PHP API는 배열을 직접 반환하므로 매핑
      if (Array.isArray(response.data)) {
        let schedules = response.data
          .map(mapPhpResponseToSchedule)
          .filter((schedule): schedule is Schedule => schedule !== null); // null 값 제거

        // 클라이언트 사이드 필터링 적용
        if (filters) {
          schedules = this.applyClientSideFilters(schedules, filters);
        }

        return schedules;
      }

      // 응답 데이터가 배열이 아닌 경우
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        let schedules = response.data.data
          .map(mapPhpResponseToSchedule)
          .filter((schedule: any): schedule is Schedule => schedule !== null); // null 값 제거

        // 클라이언트 사이드 필터링 적용
        if (filters) {
          schedules = this.applyClientSideFilters(schedules, filters);
        }

        return schedules;
      }

      // 개발 환경에서 임시 더미 데이터 제공
      return this.getDummySchedules();

      return [];
    } catch (error: any) {
      console.error('❌ 일정 목록 조회 실패:', error);

      // 개발 환경에서는 더미 데이터로 fallback
      return this.getDummySchedules();
    }
  },

  // 휴일 목록 조회
  async getHolidays(year?: number, month?: number): Promise<Holiday[]> {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', String(year));
      if (month) params.append('month', String(month));

      const url = params.toString() ? `/get_holidays.php?${params.toString()}` : '/get_holidays.php';
      const response = await apiClient.get(url);

      if (Array.isArray(response.data)) {
        const holidays = response.data.map(mapPhpResponseToHoliday);
        return holidays;
      }

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const holidays = response.data.data.map(mapPhpResponseToHoliday);
        return holidays;
      }

      // 개발 환경에서 임시 더미 데이터 제공
      return this.getDummyHolidays();

      return [];
    } catch (error: any) {
      console.error('❌ 휴일 목록 조회 실패:', error);

      // 개발 환경에서는 더미 데이터로 fallback
      return this.getDummyHolidays();
    }
  },

  // 일정 생성
  async createSchedule(data: CreateScheduleRequest): Promise<Schedule> {
    try {
      const phpData = mapScheduleToPhpRequest(data);


      const formData = new FormData();
      Object.entries(phpData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      const response = await apiClient.post('/schedule.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });


      if (response.status >= 200 && response.status < 300) {
        this.invalidateCache();

        const schedules = await this.getSchedules();
        if (schedules.length > 0) {
          const latestSchedule = schedules.reduce((latest, current) =>
            current.id > latest.id ? current : latest
          );
          return latestSchedule;
        }
      }

      throw new Error('일정 생성에 실패했습니다.');
    } catch (error: any) {
      console.error('❌ 일정 생성 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw new Error('일정을 저장하는데 실패했습니다.');
    }
  },

  // 일정 수정
  async updateSchedule(data: UpdateScheduleRequest): Promise<Schedule> {
    try {
      const phpData = mapScheduleToPhpRequest(data);


      // PHP API는 PUT 메서드를 직접 지원하지 않으므로 POST로 _method=PUT 전송
      const formData = new FormData();
      formData.append('_method', 'PUT');
      Object.entries(phpData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      const response = await apiClient.post('/schedule.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });


      if (response.status >= 200 && response.status < 300) {
        this.invalidateCache();

        const schedules = await this.getSchedules();
        const updatedSchedule = schedules.find(schedule => schedule.id === data.id);

        if (updatedSchedule) {
          return updatedSchedule;
        }
      }

      throw new Error('일정 수정에 실패했습니다.');
    } catch (error: any) {
      console.error('❌ 일정 수정 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw new Error('일정을 수정하는데 실패했습니다.');
    }
  },

  // 일정 삭제
  async deleteSchedule(id: number): Promise<void> {
    try {
      // PHP API는 DELETE 메서드를 직접 지원하지 않으므로 POST로 _method=DELETE 전송
      const formData = new FormData();
      formData.append('_method', 'DELETE');
      formData.append('id', String(id));

      const response = await apiClient.post('/schedule.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });


      if (response.status >= 200 && response.status < 300) {
        this.invalidateCache();
        return;
      }

      throw new Error('일정 삭제에 실패했습니다.');
    } catch (error) {
      console.error('❌ 일정 삭제 실패:', error);
      throw new Error('일정을 삭제하는데 실패했습니다.');
    }
  },

  // 캘린더 이벤트 형식으로 일정 조회 (공휴일 제외)
  async getCalendarEvents(filters?: ScheduleFilters): Promise<CalendarEvent[]> {
    try {
      const schedules = await this.getCachedSchedules(filters);

      // 일반 일정만 캘린더 이벤트로 변환 (공휴일 제외)
      const scheduleEvents = schedules
        .map(mapScheduleToCalendarEvent)
        .filter((event): event is CalendarEvent => event !== null);



      return scheduleEvents;
    } catch (error) {
      console.error('❌ 캘린더 이벤트 조회 실패:', error);
      throw error;
    }
  },

  // 특정 날짜 범위의 일정 조회
  async getSchedulesByDateRange(startDate: string, endDate: string): Promise<Schedule[]> {
    const filters: ScheduleFilters = {
      start_date: startDate,
      end_date: endDate
    };
    return this.getSchedules(filters);
  },

  // 전체 데이터 캐싱을 위한 메서드
  _cachedSchedules: null as Schedule[] | null,
  _cacheTimestamp: 0,
  _cacheExpiry: 2 * 60 * 1000, // 2분

  async getCachedSchedules(filters?: ScheduleFilters): Promise<Schedule[]> {
    const now = Date.now();

    // 필터가 있는 경우 캐시 사용하지 않음
    if (filters && Object.keys(filters).some(key => filters[key as keyof ScheduleFilters] && filters[key as keyof ScheduleFilters] !== 'ALL')) {
      return this.getSchedules(filters);
    }

    // 캐시가 유효한지 확인
    if (this._cachedSchedules && (now - this._cacheTimestamp) < this._cacheExpiry) {
      return this._cachedSchedules;
    }

    const schedules = await this.getSchedules();
    this._cachedSchedules = schedules;
    this._cacheTimestamp = now;

    return schedules;
  },

  // 캐시 무효화
  invalidateCache(): void {
    this._cachedSchedules = null;
    this._cacheTimestamp = 0;
  },

  // 개발용 더미 데이터
  getDummySchedules(): Schedule[] {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(today.getDate() + 2);

    return [
      {
        id: 1,
        title: '주간 회의',
        description: '팀 주간 진행 상황 공유',
        start_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')} 10:00:00`,
        end_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')} 11:00:00`,
        category: ScheduleCategory.MEETING,
        priority: SchedulePriority.HIGH,
        status: ScheduleStatus.SCHEDULED,
        location: '회의실 A',
        participants: '김철수, 이영희, 박민수',
        recurrence_type: RecurrenceType.WEEKLY,
        recurrence_end: '',
        is_all_day: false,
        created_by: 'admin',
        created_at: today.toISOString(),
      },
      {
        id: 2,
        title: '시스템 점검',
        description: '서버 정기 점검 및 업데이트',
        start_date: `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')} 02:00:00`,
        end_date: `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')} 06:00:00`,
        category: ScheduleCategory.MAINTENANCE,
        priority: SchedulePriority.HIGH,
        status: ScheduleStatus.SCHEDULED,
        location: '서버실',
        participants: '시스템관리팀',
        recurrence_type: RecurrenceType.MONTHLY,
        recurrence_end: '',
        is_all_day: false,
        created_by: 'admin',
        created_at: today.toISOString(),
      },
      {
        id: 3,
        title: '신입사원 교육',
        description: '신입사원 오리엔테이션 및 교육',
        start_date: `${nextWeek.getFullYear()}-${String(nextWeek.getMonth() + 1).padStart(2, '0')}-${String(nextWeek.getDate()).padStart(2, '0')} 09:00:00`,
        end_date: `${nextWeek.getFullYear()}-${String(nextWeek.getMonth() + 1).padStart(2, '0')}-${String(nextWeek.getDate()).padStart(2, '0')} 18:00:00`,
        category: ScheduleCategory.TRAINING,
        priority: SchedulePriority.MEDIUM,
        status: ScheduleStatus.SCHEDULED,
        location: '교육장',
        participants: '신입사원 5명',
        recurrence_type: RecurrenceType.NONE,
        recurrence_end: '',
        is_all_day: false,
        created_by: 'hr',
        created_at: today.toISOString(),
      },
      {
        id: 4,
        title: '프로젝트 회의',
        description: '새 프로젝트 기획 회의',
        start_date: `${dayAfterTomorrow.getFullYear()}-${String(dayAfterTomorrow.getMonth() + 1).padStart(2, '0')}-${String(dayAfterTomorrow.getDate()).padStart(2, '0')} 14:00:00`,
        end_date: `${dayAfterTomorrow.getFullYear()}-${String(dayAfterTomorrow.getMonth() + 1).padStart(2, '0')}-${String(dayAfterTomorrow.getDate()).padStart(2, '0')} 16:00:00`,
        category: ScheduleCategory.MEETING,
        priority: SchedulePriority.HIGH,
        status: ScheduleStatus.SCHEDULED,
        location: '회의실 B',
        participants: '개발팀 전체',
        recurrence_type: RecurrenceType.NONE,
        recurrence_end: '',
        is_all_day: false,
        created_by: 'manager',
        created_at: today.toISOString(),
      },
      {
        id: 5,
        title: '개발팀 워크샵',
        description: '팀 빌딩 및 기술 공유',
        start_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 3).padStart(2, '0')} 09:00:00`,
        end_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 3).padStart(2, '0')} 18:00:00`,
        category: ScheduleCategory.EVENT,
        priority: SchedulePriority.MEDIUM,
        status: ScheduleStatus.SCHEDULED,
        location: '세미나실',
        participants: '개발팀',
        recurrence_type: RecurrenceType.NONE,
        recurrence_end: '',
        is_all_day: true,
        created_by: 'hr',
        created_at: today.toISOString(),
      },
      {
        id: 6,
        title: '이용자 외출',
        description: '김영희님 외출',
        start_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 1).padStart(2, '0')} 14:00:00`,
        end_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 1).padStart(2, '0')} 17:00:00`,
        category: ScheduleCategory.USER_OUTING,
        priority: SchedulePriority.MEDIUM,
        status: ScheduleStatus.SCHEDULED,
        location: '병원',
        participants: '김영희, 보호자',
        recurrence_type: RecurrenceType.NONE,
        recurrence_end: '',
        is_all_day: false,
        created_by: 'staff',
        created_at: today.toISOString(),
      },
      {
        id: 7,
        title: '종사자 연가',
        description: '박민수님 연가',
        start_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 2).padStart(2, '0')} 09:00:00`,
        end_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 2).padStart(2, '0')} 18:00:00`,
        category: ScheduleCategory.STAFF_ANNUAL,
        priority: SchedulePriority.MEDIUM,
        status: ScheduleStatus.SCHEDULED,
        location: '',
        participants: '박민수',
        recurrence_type: RecurrenceType.NONE,
        recurrence_end: '',
        is_all_day: true,
        created_by: 'hr',
        created_at: today.toISOString(),
      },
      {
        id: 8,
        title: '이용자 면회',
        description: '이철수님 가족 면회',
        start_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 4).padStart(2, '0')} 15:00:00`,
        end_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 4).padStart(2, '0')} 16:00:00`,
        category: ScheduleCategory.USER_VISIT,
        priority: SchedulePriority.MEDIUM,
        status: ScheduleStatus.SCHEDULED,
        location: '면회실',
        participants: '이철수, 가족',
        recurrence_type: RecurrenceType.NONE,
        recurrence_end: '',
        is_all_day: false,
        created_by: 'staff',
        created_at: today.toISOString(),
      }
    ];
  },

  // 클라이언트 사이드 필터링 함수
  applyClientSideFilters(schedules: Schedule[], filters: ScheduleFilters): Schedule[] {
    return schedules.filter((schedule: Schedule) => {
      // 카테고리 필터
      if (filters.category && filters.category !== 'ALL' && schedule.category !== filters.category) {
        return false;
      }

      // 우선순위 필터
      if (filters.priority && filters.priority !== 'ALL' && schedule.priority !== filters.priority) {
        return false;
      }

      // 상태 필터
      if (filters.status && filters.status !== 'ALL' && schedule.status !== filters.status) {
        return false;
      }

      // 검색 필터 (제목, 설명에서 검색)
      if (filters.search && filters.search.trim()) {
        const searchTerm = filters.search.toLowerCase().trim();
        const searchableText = [
          schedule.title,
          schedule.description || ''
        ].join(' ').toLowerCase();

        if (!searchableText.includes(searchTerm)) {
          return false;
        }
      }

      // 날짜 범위 필터
      if (filters.start_date) {
        const scheduleDate = new Date(schedule.start_date);
        const filterStartDate = new Date(filters.start_date);
        if (scheduleDate < filterStartDate) {
          return false;
        }
      }

      if (filters.end_date) {
        const scheduleDate = new Date(schedule.start_date);
        const filterEndDate = new Date(filters.end_date);
        if (scheduleDate > filterEndDate) {
          return false;
        }
      }

      // 생성자 필터
      if (filters.created_by && schedule.created_by !== filters.created_by) {
        return false;
      }

      return true;
    });
  },

  getDummyHolidays(): Holiday[] {
    const currentYear = new Date().getFullYear();

    return [
      {
        id: 1,
        name: '신정',
        date: `${currentYear}-01-01`,
        type: 'national',
        is_holiday: true,
        description: '새해 첫날',
        created_at: new Date().toISOString(),
      },
      {
        id: 2,
        name: '설날',
        date: `${currentYear}-02-10`,
        type: 'national',
        is_holiday: true,
        description: '음력 1월 1일',
        created_at: new Date().toISOString(),
      },
      {
        id: 3,
        name: '어린이날',
        date: `${currentYear}-05-05`,
        type: 'national',
        is_holiday: true,
        description: '어린이날',
        created_at: new Date().toISOString(),
      },
      {
        id: 4,
        name: '추석',
        date: `${currentYear}-09-17`,
        type: 'national',
        is_holiday: true,
        description: '음력 8월 15일',
        created_at: new Date().toISOString(),
      },
      {
        id: 5,
        name: '개천절',
        date: `${currentYear}-10-03`,
        type: 'national',
        is_holiday: true,
        description: '개천절',
        created_at: new Date().toISOString(),
      },
      {
        id: 6,
        name: '한글날',
        date: `${currentYear}-10-09`,
        type: 'national',
        is_holiday: true,
        description: '한글날',
        created_at: new Date().toISOString(),
      },
      {
        id: 7,
        name: '크리스마스',
        date: `${currentYear}-12-25`,
        type: 'national',
        is_holiday: true,
        description: '성탄절',
        created_at: new Date().toISOString(),
      }
    ];
  },

  // 이용자별 일정 통계 조회
  async getUserScheduleStats(users: Array<{ id: number; name: string }>, month?: string): Promise<any[]> {
    try {

      // 지정된 월의 일정만 필터링 (month가 없으면 현재 월)
      const targetMonth = month || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

      // 일정관리와 동일한 방식으로 필터링된 데이터 가져오기
      const filters: ScheduleFilters = {
        start_date: `${targetMonth}-01`,
        end_date: `${targetMonth}-31`
      };

      const filteredSchedules = await this.getSchedules(filters);

      // 각 이용자별로 일정관리와 동일한 검색 필터를 사용하여 개별 조회
      const userStats = await Promise.all(users.map(async (user) => {

        // 일정관리와 동일한 검색 필터 사용
        const userFilters: ScheduleFilters = {
          ...filters,
          search: user.name // 일정관리에서 사용하는 search 필터
        };

        const userSchedules = await this.getSchedules(userFilters);

        // 카테고리별 통계 집계
        const stats = {
          '외출': 0,
          '외박': 0,
          '면회': 0,
          '자립': 0,
          '퇴소': 0
        };

        let totalSchedules = 0;

        // 검색된 일정들을 카테고리별로 카운팅
        userSchedules.forEach(schedule => {
          const category = schedule.category;

          if (stats.hasOwnProperty(category)) {
            stats[category as keyof typeof stats]++;
            totalSchedules++;
          }
        });


        return {
          userId: user.id,
          userName: user.name,
          stats,
          totalSchedules
        };
      }));

      return userStats;

    } catch (error) {
      console.error('❌ 이용자별 일정 통계 조회 실패:', error);
      throw error;
    }
  },

  // 기간별 이용자 일정 통계 조회
  async getUserScheduleStatsByPeriod(users: Array<{ id: number; name: string }>, startDate: string, endDate: string): Promise<any[]> {
    try {

      // 일정관리와 동일한 방식으로 필터링된 데이터 가져오기
      const filters: ScheduleFilters = {
        start_date: startDate,
        end_date: endDate
      };

      // 각 이용자별로 일정관리와 동일한 검색 필터를 사용하여 개별 조회
      const userStats = await Promise.all(users.map(async (user) => {

        // 일정관리와 동일한 검색 필터 사용
        const userFilters: ScheduleFilters = {
          ...filters,
          search: user.name // 일정관리에서 사용하는 search 필터
        };

        const userSchedules = await this.getSchedules(userFilters);

        // 카테고리별 통계 집계
        const stats = {
          '외출': 0,
          '외박': 0,
          '면회': 0,
          '자립': 0,
          '퇴소': 0
        };

        let totalSchedules = 0;

        // 검색된 일정들을 카테고리별로 카운팅
        userSchedules.forEach(schedule => {
          const category = schedule.category;

          if (stats.hasOwnProperty(category)) {
            stats[category as keyof typeof stats]++;
            totalSchedules++;
          }
        });


        return {
          userId: user.id,
          userName: user.name,
          stats,
          totalSchedules
        };
      }));

      return userStats;

    } catch (error) {
      console.error('❌ 기간별 이용자 일정 통계 조회 실패:', error);
      throw error;
    }
  }
};

export default scheduleApiService;